import React, { useEffect, useMemo, useState } from 'react';
import { REFERENCE_OPTIONS } from '../../lib/calculator';
import ManagementAssessmentForm from './ManagementAssessmentForm';
import PatientChart from './PatientChart';
import PatientStats from './PatientStats';
import AssessmentTimeline from './AssessmentTimeline';
import { useLocale } from '../LocaleProvider';

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

function formatBirthDate(value, es = false) {
  if (!value) return es ? 'Fecha de nacimiento no informada' : 'Nascimento não informado';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : (es ? 'Fecha de nacimiento no informada' : 'Nascimento não informado');
}

function apiError(payload, fallback) {
  return payload?.error || fallback;
}

export default function PatientSheet({ patient, onClose, onPatientChanged }) {
  const { locale } = useLocale();
  const es = locale === 'es';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [filters, setFilters] = useState({ from: '', to: '', reference_key: '' });
  const [draftFilters, setDraftFilters] = useState({ from: '', to: '', reference_key: '' });
  const [showAssessmentForm, setShowAssessmentForm] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [editingPatient, setEditingPatient] = useState(false);
  const [patientName, setPatientName] = useState(patient?.display_name || '');
  const [patientBirthDate, setPatientBirthDate] = useState(patient?.birth_date || '');
  const [savingPatient, setSavingPatient] = useState(false);

  useEffect(() => {
    setPatientName(patient?.display_name || '');
    setPatientBirthDate(patient?.birth_date || '');
    setEditingPatient(false);
  }, [patient?.id, patient?.display_name, patient?.birth_date]);

  useEffect(() => {
    if (!patient?.id) return undefined;
    const controller = new AbortController();
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value) query.set(key, value); });
    setLoading(true);
    setActionError('');
    fetch(`/api/management/patients/${encodeURIComponent(patient.id)}${query.toString() ? `?${query.toString()}` : ''}`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(es ? 'No fue posible cargar este registro.' : apiError(payload, 'Não foi possível carregar esta ficha.'));
        setDetail(payload);
        window.eidocAnalytics?.capture('patient_sheet_viewed', { assessment_count: payload?.assessments?.length || 0 });
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setActionError(error.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [patient?.id, filters]);

  const assessments = detail?.assessments || [];
  const referenceKeys = useMemo(() => {
    const keys = new Set((detail?.reference_keys || []).filter(Boolean));
    assessments.forEach((assessment) => { if (assessment.reference_key) keys.add(assessment.reference_key); });
    return [...keys];
  }, [detail, assessments]);

  async function refresh() {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value) query.set(key, value); });
    const response = await fetch(`/api/management/patients/${encodeURIComponent(patient.id)}${query.toString() ? `?${query.toString()}` : ''}`, { credentials: 'same-origin' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(es ? 'No fue posible actualizar este registro.' : apiError(payload, 'Não foi possível atualizar esta ficha.'));
    setDetail(payload);
  }

  async function savePatient(event) {
    event.preventDefault();
    setSavingPatient(true);
    setActionError('');
    try {
      const response = await fetch(`/api/management/patients/${encodeURIComponent(patient.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ display_name: patientName, birth_date: patientBirthDate || null }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(es ? 'No fue posible actualizar los datos.' : apiError(payload, 'Não foi possível atualizar a pessoa.'));
      window.eidocAnalytics?.capture('patient_updated');
      setEditingPatient(false);
      onPatientChanged?.(payload.patient);
    } catch (error) {
      setActionError(error.message);
    } finally {
      setSavingPatient(false);
    }
  }

  async function deleteAssessment(assessment) {
    if (typeof window !== 'undefined' && !window.confirm(es ? '¿Eliminar esta evaluación del historial? Esta acción no se puede deshacer.' : 'Excluir esta avaliação do histórico? Essa ação não pode ser desfeita.')) return;
    setActionError('');
    try {
      const response = await fetch(`/api/management/assessments/${encodeURIComponent(assessment.id)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ confirm: true }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(es ? 'No fue posible eliminar la evaluación.' : apiError(payload, 'Não foi possível excluir a avaliação.'));
      window.eidocAnalytics?.capture('assessment_deleted');
      await refresh();
      onPatientChanged?.();
    } catch (error) {
      setActionError(error.message);
    }
  }

  async function deletePatient() {
    if (typeof window !== 'undefined' && !window.confirm(es ? `¿Eliminar a ${patient.display_name} y todo su historial? Esta acción no se puede deshacer.` : `Excluir ${patient.display_name} e todo o seu histórico? Essa ação não pode ser desfeita.`)) return;
    setActionError('');
    try {
      const response = await fetch(`/api/management/patients/${encodeURIComponent(patient.id)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ confirm: true }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(es ? 'No fue posible eliminar a la persona.' : apiError(payload, 'Não foi possível excluir a pessoa.'));
      window.eidocAnalytics?.capture('patient_deleted');
      onPatientChanged?.();
      onClose?.();
    } catch (error) {
      setActionError(error.message);
    }
  }

  function openNewAssessment() {
    window.eidocAnalytics?.capture('assessment_form_opened', { surface: 'patient_sheet' });
    setEditingAssessment(null);
    setShowAssessmentForm(true);
    setActionError('');
    setActionNotice('');
  }

  function openEditAssessment(assessment) {
    window.eidocAnalytics?.capture('assessment_edit_opened');
    setEditingAssessment(assessment);
    setShowAssessmentForm(true);
    setActionError('');
  }

  async function handleAssessmentSaved(result) {
    setShowAssessmentForm(false);
    setEditingAssessment(null);
    if (result?.audit_warning) {
      setActionNotice(es ? 'La evaluación se guardó, pero no pudimos registrar la actividad. Ya está en el historial; no intentes guardarla de nuevo.' : 'A avaliação foi salva, mas não conseguimos registrar a atividade. Ela já está no histórico; não tente salvá-la novamente.');
    }
    await refresh();
    onPatientChanged?.();
  }

  function applyFilters(event) {
    event.preventDefault();
    window.eidocAnalytics?.capture('patient_timeline_filtered', { has_from: Boolean(filterFrom), has_to: Boolean(filterTo), has_reference: Boolean(filterReference.trim()) });
    setFilters({ ...draftFilters });
  }

  function clearFilters() {
    const empty = { from: '', to: '', reference_key: '' };
    setDraftFilters(empty);
    setFilters(empty);
  }

  if (!patient) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[rgba(23,58,84,0.34)]" role="dialog" aria-modal="true" aria-labelledby="patient-sheet-title">
      <button type="button" aria-label={es ? 'Cerrar ficha' : 'Fechar ficha'} onClick={onClose} className="absolute inset-0 cursor-default" />
      <aside className="relative z-10 flex h-full w-full max-w-3xl flex-col overflow-y-auto bg-[var(--background)] shadow-2xl">
        <header className="sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--surface)] px-5 py-4 md:px-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--accent-tint)] text-sm font-bold text-[var(--accent)]" aria-hidden="true">
                {initials(patient.display_name)}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[var(--text-muted)]">{es ? 'Ficha de la persona' : 'Ficha da pessoa'}</p>
                <h2 id="patient-sheet-title" className="mt-1 truncate text-2xl font-semibold text-[var(--primary)]">{patient.display_name}</h2>
                <p className="mt-1 text-sm text-[var(--text-muted)]">{formatBirthDate(patient.birth_date, es)}</p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <button type="button" onClick={openNewAssessment} className="rounded-xl bg-[var(--accent)] px-4 py-2 text-sm font-bold text-[var(--on-accent)]">{es ? 'Registrar evaluación' : 'Registrar avaliação'}</button>
              <button type="button" onClick={onClose} aria-label={es ? 'Cerrar ficha' : 'Fechar ficha'} className="flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--border)] text-2xl leading-none text-[var(--text-muted)] hover:bg-[var(--surface-soft)]">×</button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={() => setEditingPatient((value) => !value)} className="rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-bold text-[var(--primary)]">{es ? (editingPatient ? 'Cancelar edición' : 'Editar datos') : (editingPatient ? 'Cancelar edição' : 'Editar cadastro')}</button>
            <button type="button" onClick={deletePatient} className="rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-bold text-[var(--danger-text)]">{es ? 'Eliminar persona' : 'Excluir pessoa'}</button>
          </div>
        </header>

        <div className="space-y-5 px-5 py-5 md:px-8 md:py-7">
          {actionError && <p role="alert" className="rounded-xl bg-[var(--danger-bg)] px-4 py-3 text-sm font-semibold text-[var(--danger-text)]">{actionError}</p>}
          {actionNotice && <p role="status" className="rounded-xl bg-[var(--warning-bg)] px-4 py-3 text-sm font-semibold text-[var(--warning-text)]">{actionNotice}</p>}

          {editingPatient && (
            <form onSubmit={savePatient} className="card p-5">
              <h3 className="font-sans font-semibold text-[var(--primary)]">{es ? 'Datos de la persona' : 'Dados da pessoa'}</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-bold text-[var(--primary)]">{es ? 'Nombre' : 'Nome'}
                  <input required minLength={2} maxLength={160} value={patientName} onChange={(event) => setPatientName(event.target.value)} className="input-field mt-2" />
                </label>
                <label className="text-sm font-bold text-[var(--primary)]">{es ? 'Fecha de nacimiento' : 'Data de nascimento'}
                  <input type="date" value={patientBirthDate} onChange={(event) => setPatientBirthDate(event.target.value)} className="input-field mt-2" />
                </label>
              </div>
              <button type="submit" disabled={savingPatient} className="mt-4 rounded-xl bg-[var(--accent)] px-4 py-3 font-bold text-[var(--on-accent)] disabled:opacity-60">{savingPatient ? (es ? 'Guardando…' : 'Salvando…') : (es ? 'Guardar datos' : 'Salvar cadastro')}</button>
            </form>
          )}

          <form onSubmit={applyFilters} className="card p-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="font-sans font-semibold text-[var(--primary)]">{es ? 'Evolución' : 'Evolução'}</h3>
                <p className="mt-1 text-sm text-[var(--text-muted)]">{es ? 'Compara aplicaciones con el mismo intervalo de fechas y referencia.' : 'Compare aplicações usando o mesmo recorte de datas e referência.'}</p>
              </div>
              <button type="button" onClick={clearFilters} className="text-sm text-[var(--primary)] hover:underline">{es ? 'Borrar filtros' : 'Limpar filtros'}</button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <label className="text-sm font-bold text-[var(--primary)]">{es ? 'Desde' : 'De'}
                <input type="date" value={draftFilters.from} onChange={(event) => setDraftFilters((current) => ({ ...current, from: event.target.value }))} className="input-field mt-2" />
              </label>
              <label className="text-sm font-bold text-[var(--primary)]">{es ? 'Hasta' : 'Até'}
                <input type="date" value={draftFilters.to} onChange={(event) => setDraftFilters((current) => ({ ...current, to: event.target.value }))} className="input-field mt-2" />
              </label>
              <label className="text-sm font-bold text-[var(--primary)]">{es ? 'Referencia' : 'Referência'}
                <select value={draftFilters.reference_key} onChange={(event) => setDraftFilters((current) => ({ ...current, reference_key: event.target.value }))} className="input-field mt-2">
                  <option value="">{es ? 'Todas' : 'Todas'}</option>
                  {(referenceKeys.length ? referenceKeys : REFERENCE_OPTIONS.map((option) => option.value)).map((key) => (
                    <option key={key} value={key}>{REFERENCE_OPTIONS.find((option) => option.value === key)?.label || key}</option>
                  ))}
                </select>
              </label>
            </div>
            <button type="submit" className="mt-4 rounded-xl border border-[var(--accent)] px-4 py-2 text-sm font-bold text-[var(--accent)]">{es ? 'Aplicar filtros' : 'Aplicar filtros'}</button>
          </form>

          {loading ? (
            <div className="card p-8 text-center text-sm text-[var(--text-muted)]">{es ? 'Cargando historial…' : 'Carregando histórico…'}</div>
          ) : (
            <>
              <PatientStats stats={detail?.stats || {}} />
              <PatientChart assessments={assessments} />
              <section className="card p-5">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h3 className="font-sans text-xl font-semibold text-[var(--primary)]">{es ? 'Historial' : 'Histórico'}</h3>
                    <p className="mt-1 text-sm text-[var(--text-muted)]">{assessments.length ? `${assessments.length} ${es ? (assessments.length === 1 ? 'registro' : 'registros') : (assessments.length === 1 ? 'registro' : 'registros')} ${es ? 'en este intervalo' : 'neste recorte'}` : (es ? 'No hay registros en este intervalo' : 'Nenhum registro neste recorte')}</p>
                  </div>
                  {!assessments.length && <button type="button" onClick={openNewAssessment} className="rounded-xl bg-[var(--accent)] px-4 py-2 text-sm font-bold text-[var(--on-accent)]">{es ? 'Registrar evaluación' : 'Registrar avaliação'}</button>}
                </div>
                <div className="mt-5"><AssessmentTimeline assessments={assessments} onEdit={openEditAssessment} onDelete={deleteAssessment} /></div>
              </section>
            </>
          )}
        </div>
      </aside>

      {showAssessmentForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(23,58,84,0.42)] px-4 py-5" role="dialog" aria-modal="true" aria-labelledby="management-assessment-title">
          <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto sheet p-5 md:p-7">
            <ManagementAssessmentForm
              key={editingAssessment?.id || 'new'}
              patients={[patient]}
              initialPatientId={patient.id}
              initialAssessment={editingAssessment}
              onSaved={handleAssessmentSaved}
              onCancel={() => { setShowAssessmentForm(false); setEditingAssessment(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
